import React from 'react';
import fhirpath from 'fhirpath';

import { PassoExecutado } from '@/models/PassoExecutado';
import Button from '@/components/Button';
import Information from '@/components/Information';

import { ArtifactFormOverrides } from '@/features/artifacts/components/ArtifactForm/ArtifactForm.types';
import { useArtifactFormConfig } from '@/features/artifacts/hooks/useArtifactFormConfig';
import TipoArtefatoLabel from '@/features/prescricao-livre/components/TipoArtefatoLabel';
import Feature from '@/features/features/components/Feature';
import { usePrescricaoLivreContext } from '@/features/prescricao-livre/providers/PrescricaoLivreProvider';
import { getFHIRResourceTitle } from '@/features/fhir/helpers/title';
import { Paragraph } from '@/features/artifacts/components/ArtifactForm/ArtifactField.widgets';

import CardSolicitacao, {
  CardSolicitacaoProxyProps,
} from '../../CardSolicitacao';
import SolicitacaoList from '..';
import { Heading } from '../../SolicitacaoListContainer/styles';
import { PrescricaoMedicaHeading } from './styles';

const Form: ArtifactFormOverrides = [
  {
    name: 'dosageInstruction',
    label: 'Posologia',
    required: true,
    attrs: { autoFocus: true },
    containerStyle: { flex: '0 1 100%' },
  },
  {
    name: 'additionalInstruction',
    label: 'Observações',
    containerStyle: { flex: '0 1 100%' },
    widget: Paragraph,
  },
];

const CardPrescricaoMedica: React.FC<CardSolicitacaoProxyProps> = ({
  solicitacao,
  ...rest
}) => {
  const form = useArtifactFormConfig('prescricao-medica', Form);

  const title = React.useMemo(() => {
    return getFHIRResourceTitle(solicitacao.pedido) || 'Prescrição Médica';
  }, [solicitacao.pedido]);

  const description = React.useMemo(() => {
    const dosageInstruction = fhirpath.evaluate(
      solicitacao.pedido,
      'dosageInstruction.text',
    );

    if (dosageInstruction.length) {
      return dosageInstruction[0];
    }

    return '';
  }, [solicitacao.pedido]);

  const additionalInstruction = React.useMemo(() => {
    return fhirpath.evaluate(
      solicitacao.pedido,
      'MedicationRequest.dosageInstruction.additionalInstruction.text',
    )[0];
  }, [solicitacao.pedido]);

  return (
    <CardSolicitacao
      title={title}
      description={description}
      solicitacao={solicitacao}
      form={form}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {additionalInstruction && (
        <Information type="info">{additionalInstruction}</Information>
      )}
    </CardSolicitacao>
  );
};

interface Props {
  passo: PassoExecutado;
}
const SolicitacaoListPrescricaoMedica: React.FC<Props> = ({ passo }) => {
  const { toggleMenu, setGeneralType } = usePrescricaoLivreContext();

  const handleOpenManipuladosMenu = React.useCallback(() => {
    setGeneralType('manipulado');
    toggleMenu(true);
  }, [setGeneralType, toggleMenu]);

  return (
    <SolicitacaoList
      passo={passo}
      tipo="prescricao-medica"
      renderHeading={() => (
        <PrescricaoMedicaHeading>
          <Heading>
            <TipoArtefatoLabel tipo="prescricao-medica" />
          </Heading>
          <Feature name="prescricaolivre.medicamentos.manipulados">
            <Button theme="tertiary" onClick={handleOpenManipuladosMenu}>
              <TipoArtefatoLabel tipo="manipulado" />
            </Button>
          </Feature>
        </PrescricaoMedicaHeading>
      )}
      CardSolicitacaoComponent={CardPrescricaoMedica}
      groupTitle="Selecione os medicamentos que você quer solicitar no seu prontuário."
      addConfig={{
        title: 'Adicionar medicamento complementar',
      }}
    />
  );
};

export default SolicitacaoListPrescricaoMedica;
